const usd = new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' })
const usdCompact = new Intl.NumberFormat('en-US', {
  style: 'currency',
  currency: 'USD',
  notation: 'compact',
  maximumFractionDigits: 2,
})

export function formatUsd(value: number | null | undefined, compact = false) {
  if (value === null || value === undefined || !Number.isFinite(value)) return '–'
  if (compact && Math.abs(value) >= 10_000) return usdCompact.format(value)
  if (Math.abs(value) > 0 && Math.abs(value) < 0.01) return '<$0.01'
  return usd.format(value)
}

const usdWhole = new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 })

/** Whole dollars: a card header's total on a phone, where the cents would push the buttons onto a second row (Nov 3 2026). */
export function formatUsdWhole(value: number | null | undefined) {
  if (value === null || value === undefined || !Number.isFinite(value)) return '–'
  return usdWhole.format(value)
}

/** A unit price: cents for normal prices, significant digits for sub-cent tokens. */
export function formatPrice(value: number | null | undefined) {
  if (value === null || value === undefined || !Number.isFinite(value) || value <= 0) return '–'
  if (value >= 1) return usd.format(value)
  if (value >= 0.01) return `$${value.toFixed(4)}`
  return `$${Number(value.toPrecision(3)).toString()}`
}

/** Token amount from base units; trims trailing zeros, keeps 2–6 decimals. */
export function formatRawAmount(raw: string | bigint | null | undefined, decimals: number, maxDecimals = 6) {
  if (raw === null || raw === undefined) return '–'
  const value = Number(BigInt(raw)) / 10 ** decimals
  return formatAmount(value, maxDecimals)
}

export function formatAmount(value: number | null | undefined, maxDecimals = 6) {
  if (value === null || value === undefined || !Number.isFinite(value)) return '–'
  const abs = Math.abs(value)
  const decimals = abs >= 1000 ? 2 : abs >= 1 ? 4 : maxDecimals
  return value.toLocaleString('en-US', { maximumFractionDigits: decimals })
}

/**
 * SOL shows up to 5 places whatever its size (John, Oct 30 2026: "5 places
 * for SOL as a general rule of thumb"); a dust amount keeps two significant
 * digits so it never reads as 0.
 */
export const SOL_PLACES = 5
export function formatSol(value: number | null | undefined) {
  if (value === null || value === undefined || !Number.isFinite(value)) return '–'
  const abs = Math.abs(value)
  // under 0.00001 SOL one figure is enough: 0.00002, not 0.000019634 (John, Oct 31 2026)
  if (abs > 0 && abs < 10 ** -SOL_PLACES) return value.toLocaleString('en-US', { maximumSignificantDigits: 1 })
  return value.toLocaleString('en-US', { maximumFractionDigits: SOL_PLACES })
}

/** A fee line's amount: SOL to five places (one figure when tiny), anything else to six at most. */
function formatFeeAmount(value: number, symbol: string, decimals: number) {
  return isSol(symbol) ? formatSol(value) : formatAmount(value, Math.min(decimals, 6))
}

const isSol = (symbol: string | null | undefined) => {
  const s = (symbol ?? '').toUpperCase()
  return s === 'SOL' || s === 'WSOL'
}

/** an amount under its asset's rule: SOL to 5 places, anything else as formatAmount */
export function formatTokenAmount(value: number | null | undefined, symbol: string | null | undefined, maxDecimals = 6) {
  return isSol(symbol) ? formatSol(value) : formatAmount(value, maxDecimals)
}

export function formatRawTokenAmount(raw: string | bigint | null | undefined, decimals: number, symbol: string | null | undefined, maxDecimals = 6) {
  if (raw === null || raw === undefined) return '–'
  return formatTokenAmount(Number(BigInt(raw)) / 10 ** decimals, symbol, maxDecimals)
}

/**
 * Thousands, millions and billions to one decimal (John, Nov 1 2026: "$1.1k",
 * "500k"): the transactions and holdings tables; never the portfolio total
 * at the top. Under a thousand the usual formatting holds.
 */
export function formatShort(value: number): string {
  const sign = value < 0 ? '-' : ''
  const abs = Math.abs(value)
  for (const [suffix, size] of [['k', 1e3], ['m', 1e6], ['b', 1e9], ['t', 1e12]] as const) {
    const scaled = Math.round((abs / size) * 10) / 10
    if (scaled < 1000 || suffix === 't') return `${sign}${scaled.toLocaleString('en-US', { maximumFractionDigits: 1 })}${suffix}`
  }
  return `${sign}${abs.toLocaleString('en-US')}`
}

export function formatUsdShort(value: number | null | undefined) {
  if (value === null || value === undefined || !Number.isFinite(value)) return '–'
  if (Math.abs(value) < 1000) return formatUsd(value)
  return `${value < 0 ? '-' : ''}$${formatShort(Math.abs(value))}`
}

export function formatTokenAmountShort(value: number | null | undefined, symbol: string | null | undefined, maxDecimals = 6) {
  if (value === null || value === undefined || !Number.isFinite(value)) return '–'
  return Math.abs(value) >= 1000 ? formatShort(value) : formatTokenAmount(value, symbol, maxDecimals)
}

export function formatPct(value: number | null | undefined, signed = true) {
  if (value === null || value === undefined || !Number.isFinite(value)) return '–'
  const sign = signed && value > 0 ? '+' : ''
  return `${sign}${value.toFixed(2)}%`
}

export function shortAddress(address: string, chars = 4) {
  if (address.length <= chars * 2 + 1) return address
  return `${address.slice(0, chars)}…${address.slice(-chars)}`
}

export function formatDuration(seconds: number) {
  if (seconds < 60) return `${seconds}s`
  if (seconds < 3600) return `${Math.round(seconds / 60)} min`
  if (seconds < 86400) {
    const h = seconds / 3600
    return `${Number.isInteger(h) ? h : h.toFixed(1)} h`
  }
  const d = seconds / 86400
  return `${Number.isInteger(d) ? d : d.toFixed(1)} d`
}

export function formatTime(iso: string | null | undefined) {
  if (!iso) return '–'
  return new Date(iso).toLocaleString('en-US', { dateStyle: 'medium', timeStyle: 'short' })
}

/**
 * How long ago, as the transactions table reads it (John, Nov 3 2026): "30s", "5m", "25m", "1h", "20h", "1D", "24D".
 * Whole units rounded down, so a row counts 30s, 31s, 32s and turns 1m on its sixtieth second.
 */
export function formatAge(iso: string | null | undefined, now = Date.now()) {
  if (!iso) return '–'
  const seconds = Math.floor((now - Date.parse(iso)) / 1000)
  if (!Number.isFinite(seconds)) return '–'
  if (seconds < 60) return `${Math.max(0, seconds)}s`
  const minutes = Math.floor(seconds / 60)
  if (minutes < 60) return `${minutes}m`
  const hours = Math.floor(minutes / 60)
  if (hours < 24) return `${hours}h`
  return `${Math.floor(hours / 24)}D`
}

/** "10/31/26 at 12:26 PM": a strategy card's next order (John, Oct 31 2026) */
export function formatShortWhen(iso: string | null | undefined) {
  if (!iso) return '–'
  const at = new Date(iso)
  const date = at.toLocaleDateString('en-US', { month: 'numeric', day: 'numeric', year: '2-digit' })
  const time = at.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' })
  return `${date} at ${time}`
}

export const LAMPORTS_PER_SOL = 1_000_000_000

/** "1 part", "3 parts" */
export function plural(count: number, noun: string, pluralNoun = `${noun}s`) {
  return `${count} ${count === 1 ? noun : pluralNoun}`
}

type FeeLine = { label: string; symbol: string; mint: string | null; decimals: number; amountRaw: string; usd: number | null }

/** The receipt's total: one amount per currency, and the dollar sum when every line has one. */
export function feeTotals(items: FeeLine[]) {
  const byMint = new Map<string | null, { symbol: string; decimals: number; raw: bigint }>()
  for (const item of items) {
    const entry = byMint.get(item.mint) ?? { symbol: item.symbol, decimals: item.decimals, raw: 0n }
    entry.raw += BigInt(item.amountRaw)
    byMint.set(item.mint, entry)
  }
  const amounts = [...byMint.values()].map((e) => `${formatFeeAmount(Number(e.raw) / 10 ** e.decimals, e.symbol, e.decimals)} ${e.symbol}`).join(' + ')
  const usd = items.every((item) => item.usd !== null) ? items.reduce((sum, item) => sum + (item.usd ?? 0), 0) : null
  return { amounts, usd }
}

/** One line for a table cell: the total, with its dollar value; null when the fees are not known. */
export function formatFeeTotal(items: FeeLine[] | null | undefined) {
  if (!items || items.length === 0) return null
  const total = feeTotals(items)
  return total.usd !== null ? `${total.amounts} (${formatUsd(total.usd)})` : total.amounts
}

/**
 * A table cell's fee (John, Nov 3 2026: the amounts with their dollars in brackets made the Fee column so wide the
 * Withdraws tab spilled past the card): the dollar figure when every line has one, the amounts otherwise; null when
 * the fees are not known. The receipt itself goes in the tooltip.
 */
export function formatFeeShort(items: FeeLine[] | null | undefined) {
  if (!items || items.length === 0) return null
  const total = feeTotals(items)
  return total.usd !== null ? formatUsd(total.usd) : total.amounts
}

/** The receipt as plain lines, for a tooltip. */
export function feeReceiptText(items: FeeLine[] | null | undefined) {
  if (!items || items.length === 0) return null
  return items
    .map((item) => `${item.label}: ${formatFeeAmount(Number(item.amountRaw) / 10 ** item.decimals, item.symbol, item.decimals)} ${item.symbol}${item.usd !== null ? ` (${formatUsd(item.usd)})` : ''}`)
    .join('\n')
}

/** the scale each shorthand suffix stands for: 600k, 2.5m (also mm, mn, mil, million), 1.2b (bn, billion), 3t */
const SHORTHAND_SCALES: Record<string, number> = {
  '': 1,
  k: 1e3,
  m: 1e6,
  mm: 1e6,
  mn: 1e6,
  mil: 1e6,
  million: 1e6,
  b: 1e9,
  bn: 1e9,
  bil: 1e9,
  billion: 1e9,
  t: 1e12,
  tn: 1e12,
  trillion: 1e12,
}

/** every scale suffix the shorthand reads, so an input can refuse letters that cannot become one */
export const SHORTHAND_SUFFIXES = Object.keys(SHORTHAND_SCALES).filter(Boolean)

/**
 * A dollar figure as people type it (John, Nov 2 2026): "2.5m", "600k", "1.2B", "$2,480,000".
 * Blank gives undefined; text that is not a figure gives null, so a form can say so.
 */
export function parseUsdShorthand(value: string): number | undefined | null {
  // a comma before one or two digits is a decimal point ("2,5m"); before three it groups thousands ("2,500,000")
  const text = value
    .toLowerCase()
    .replace(/,(?=\d{1,2}(?!\d))/g, '.')
    .replace(/[$,\s_]/g, '')
  if (!text) return undefined
  const match = /^(\d+\.?\d*|\.\d+)([a-z]*)$/.exec(text)
  if (!match) return null
  const scale = SHORTHAND_SCALES[match[2]]
  if (scale === undefined) return null
  // shift the decimal point in text, so 2.48m is exactly 2,480,000 and not a float product
  const [whole, fraction = ''] = match[1].split('.')
  const digits = Number(`${whole}${fraction}` || '0')
  const exponent = Math.round(Math.log10(scale)) - fraction.length
  const usd = exponent >= 0 ? digits * 10 ** exponent : digits / 10 ** -exponent
  return Number.isFinite(usd) && usd > 0 ? usd : null
}

/** The shorthand an input shows for a saved figure: 2480000 → "2.48m", 600000 → "600k"; the exact digits when the shorthand would round */
export function formatUsdShorthand(value: number): string {
  const [scale, suffix] = value >= 1e12 ? [1e12, 't'] : value >= 1e9 ? [1e9, 'b'] : value >= 1e6 ? [1e6, 'm'] : value >= 1e3 ? [1e3, 'k'] : [1, '']
  const short = `${Number((value / scale).toFixed(2))}${suffix}`
  return parseUsdShorthand(short) === value ? short : String(value)
}

/** A market cap range as a row reads it: "Any", "$1M to $5M", "Over $1M", "Under $5M" */
export function formatMarketCapRange(min: number | null | undefined, max: number | null | undefined) {
  if (min != null && max != null) return `${formatUsd(min, true)} to ${formatUsd(max, true)}`
  if (min != null) return `Over ${formatUsd(min, true)}`
  if (max != null) return `Under ${formatUsd(max, true)}`
  return 'Any'
}
